// Day-first date entry: Day, Month (in words), Year, with the date read back in words underneath.
// Replaces the browser's own date box, which reads month first on a US-English computer.
// value / onChange use the same YYYY-MM-DD string as before, so stored answers, PDFs and file names
// don't change. While the entry is incomplete or not a real date, onChange gets "" and onProblem
// gets the message (so a form can stop the family submitting a half-typed date).
import React, { useEffect, useRef, useState } from "react";
import { MONTHS, inWords, isoFromParts, partsFromIso, problemWith, todayIso } from "../lib/dayFirst";

const row = { display: "flex", gap: "0.5rem", alignItems: "stretch", flexWrap: "wrap" };
const words = { marginTop: "0.35rem", fontSize: "0.9rem", color: "#334155" };
const problem = { marginTop: "0.35rem", fontSize: "0.85rem", color: "#b42318" };

export default function DayFirstDate({
  id,
  value,
  onChange,
  onProblem,
  notFuture = false,
  readOnly = false,
  inputClassName = "",
  inputStyle,
  testId,
  ariaInvalid = false,
  label,
}) {
  const fromValue = partsFromIso(value) || { day: "", month: "", year: "" };
  const [parts, setParts] = useState(fromValue);
  const lastSent = useRef(partsFromIso(value) ? value : "");

  // Follow the stored value when it changes from outside (a resumed draft, a reset).
  useEffect(() => {
    const p = partsFromIso(value);
    if (p && value !== lastSent.current) {
      lastSent.current = value;
      setParts(p);
    } else if (!value && lastSent.current) {
      lastSent.current = "";
      setParts({ day: "", month: "", year: "" });
    }
  }, [value]);

  const msg = problemWith(parts, { notFuture, today: todayIso() });

  const problemRef = useRef(onProblem);
  problemRef.current = onProblem;
  useEffect(() => {
    if (problemRef.current) problemRef.current(msg || null);
  }, [msg]);

  if (readOnly) {
    return (
      <div
        id={id}
        className={inputClassName}
        style={{ ...(inputStyle || {}), background: "#f1f5f9" }}
        data-testid={testId}
        aria-readonly="true"
      >
        {inWords(value) || "-"}
      </div>
    );
  }

  const update = (patch) => {
    const next = { ...parts, ...patch };
    setParts(next);
    const iso = isoFromParts(next.day, next.month, next.year);
    const ok = iso && !problemWith(next, { notFuture, today: todayIso() });
    const send = ok ? iso : "";
    if (send !== lastSent.current) {
      lastSent.current = send;
      onChange(send);
    }
  };

  const shown = !msg && isoFromParts(parts.day, parts.month, parts.year);
  const name = label || "Date";

  return (
    <div data-testid={testId ? `${testId}-group` : undefined} role="group" aria-label={name}>
      <div style={row}>
        <input
          id={id}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          maxLength={2}
          placeholder="Day"
          aria-label={`${name}: day`}
          aria-invalid={ariaInvalid || !!msg}
          className={inputClassName}
          style={{ ...(inputStyle || {}), flex: "0 0 4.5rem", width: "4.5rem", minWidth: 0, boxSizing: "border-box" }}
          value={parts.day}
          onChange={(e) => update({ day: e.target.value.replace(/\D/g, "") })}
          data-testid={testId ? `${testId}-day` : undefined}
        />
        <select
          aria-label={`${name}: month`}
          aria-invalid={ariaInvalid || !!msg}
          className={inputClassName}
          style={{ ...(inputStyle || {}), flex: "1 1 8rem", minWidth: "8rem", maxWidth: "14rem", boxSizing: "border-box" }}
          value={parts.month}
          onChange={(e) => update({ month: e.target.value })}
          data-testid={testId ? `${testId}-month` : undefined}
        >
          <option value="">Month</option>
          {MONTHS.map((m, i) => (
            <option key={m} value={String(i + 1)}>{m}</option>
          ))}
        </select>
        <input
          type="text"
          inputMode="numeric"
          autoComplete="off"
          maxLength={4}
          placeholder="Year"
          aria-label={`${name}: year`}
          aria-invalid={ariaInvalid || !!msg}
          className={inputClassName}
          style={{ ...(inputStyle || {}), flex: "0 0 5.5rem", width: "5.5rem", minWidth: 0, boxSizing: "border-box" }}
          value={parts.year}
          onChange={(e) => update({ year: e.target.value.replace(/\D/g, "") })}
          data-testid={testId ? `${testId}-year` : undefined}
        />
      </div>
      {msg ? (
        <div style={problem} role="alert" data-testid={testId ? `${testId}-problem` : undefined}>{msg}</div>
      ) : shown ? (
        <div style={words} aria-live="polite" data-testid={testId ? `${testId}-words` : undefined}>{inWords(shown)}</div>
      ) : null}
    </div>
  );
}
